import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    List,
    DataTable,
    NumberField,
    TextField,
    TextInput,
    SelectInput,
    TopToolbar,
    ExportButton,
    ColumnsButton,
    FilterButton,
    CreateButton,
    DateField,
    useRecordContext,
    useDataProvider,
    useNotify,
} from 'react-admin';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { KivoaiVideoRecord } from './types';
import { isCfStreamUid } from './validators';


const KivoaiVideoListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ColumnsButton />
        <ExportButton />
    </TopToolbar>
);

const MediaPreview = () => {
    const record = useRecordContext<KivoaiVideoRecord>();

    if (!record) return null;
    const uid = record.uid;
    const iframeUrl = uid ? `https://iframe.videodelivery.net/${uid}` : undefined;

    return (
        iframeUrl ? (
            <Box
                component="iframe"
                src={iframeUrl}
                title={record.title || record.key || `KivoAI Video #${record.id}`}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                sx={{
                    width: 240,
                    height: 135,
                    border: 0,
                    borderRadius: 1,
                    backgroundColor: 'black',
                }}
            />
        ) : (
            <Box
                sx={{
                    width: 240,
                    height: 135,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                }}
            >
                No Video
            </Box>
        )
    );
};

const Column = DataTable.Col<KivoaiVideoRecord>;

const CoachField = ({ choices }: { choices: Array<any> }) => {
    const record = useRecordContext<KivoaiVideoRecord>();
    if (!record) return null;
    const item = choices.find((c: any) => c.id === record.userId || c.id == record.userId);
    return <span>{item ? item.name : record.userId}</span>;
};

const StreamStatusField = () => {
    const record = useRecordContext<KivoaiVideoRecord>();
    if (!record) return null;
    const hasUid = Boolean(record.uid && record.uid.trim());
    const isValid = isCfStreamUid(record.uid);

    const statusLabel = !hasUid
        ? 'Missing UID'
        : isValid
          ? 'Ready'
          : 'Invalid UID';

    const statusColor = !hasUid ? '#8b1e1e' : isValid ? '#1b5e20' : '#7a4500';
    const statusBg = !hasUid ? '#ffebee' : isValid ? '#e8f5e9' : '#fff3e0';

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                px: 1,
                py: 0.3,
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 600,
                color: statusColor,
                backgroundColor: statusBg,
            }}
        >
            {statusLabel}
        </Box>
    );
};

const UidField = () => {
    const record = useRecordContext<KivoaiVideoRecord>();
    const notify = useNotify();
    if (!record) return null;

    const uid = record.uid || '';
    const isValid = isCfStreamUid(uid);

    const copyUid = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!uid) {
            notify('UID is empty', { type: 'warning' });
            return;
        }

        try {
            await navigator.clipboard.writeText(uid);
            notify('UID copied', { type: 'info' });
        } catch {
            notify('Copy failed', { type: 'warning' });
        }
    };

    const openPlayer = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!uid) {
            notify('UID is empty', { type: 'warning' });
            return;
        }

        window.open(`https://iframe.videodelivery.net/${uid}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Box
                component="span"
                sx={{
                    fontFamily: 'monospace',
                    color: uid && !isValid ? '#d32f2f' : 'inherit',
                    fontWeight: uid && !isValid ? 600 : 400,
                }}
            >
                {uid || '-'}
            </Box>
            <Tooltip title="Copy UID">
                <span>
                    <IconButton size="small" onClick={copyUid} disabled={!uid}>
                        <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Open player">
                <span>
                    <IconButton size="small" onClick={openPlayer} disabled={!uid}>
                        <OpenInNewIcon fontSize="inherit" />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
};

const KivoaiVideoListComponent = () => {
    const dataProvider = useDataProvider();
    const [coachChoices, setCoachChoices] = useState<Array<any>>([]);

    useEffect(() => {
        let mounted = true;
        dataProvider
            .getList('admin/dropdown/coaches', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: '', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => {
                if (mounted) setCoachChoices(data || []);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, [dataProvider]);

    const kivoaiVideoFilters = [
        <SelectInput key="userId" label="Coach" source="userId" choices={coachChoices} />,
        <TextInput key="uid" label="CF Stream UID" source="uid" alwaysOn />,
        <SelectInput
            key="streamStatus"
            label="Stream Status"
            source="streamStatus"
            choices={[
                { id: '', name: 'All' },
                { id: 'ready', name: 'Ready' },
                { id: 'missing', name: 'Missing UID' },
                { id: 'invalid', name: 'Invalid UID' },
            ]}
            alwaysOn
        />,
    ];

    return (
        <List
            filters={kivoaiVideoFilters}
            actions={<KivoaiVideoListActions />}
            perPage={25}
            sort={{ field: 'id', order: 'DESC' }}
        >
            <DataTable rowClick="edit" bulkActionButtons={false}>
                <Column source="id" label="ID">
                    <NumberField source="id" />
                </Column>
                <Column source="uid" label="Video">
                    <MediaPreview />
                </Column>
                <Column source="key" label="Key">
                    <TextField source="key" />
                </Column>
                <Column source="userId" label="Coach">
                    <CoachField choices={coachChoices} />
                </Column>
                <Column source="title" label="Title">
                    <TextField source="title" />
                </Column>
                <Column source="description" label="Description">
                    <TextField source="description" />
                </Column>
                <Column source="uid" label="CF Stream UID">
                    <UidField />
                </Column>
                <Column source="uid" label="Play Status">
                    <StreamStatusField />
                </Column>
                <Column source="createTime" label="Created Time">
                    <DateField source="createTime" showTime />
                </Column>
            </DataTable>
        </List>
    );
};

const KivoaiVideoList = () => <KivoaiVideoListComponent />;

export default KivoaiVideoList;
